/**
 * The actual data for each type of synced item is stored in IndexedDB.
 * For each table we also store a small piece of meta data in local storage to say when that table was last synced.
 * This class is for managing that meta data.
 */
class SyncStateManager {
    /**
     * @param {LocalStorageDataStore} localStorageDataStore
     */
    constructor(localStorageDataStore) {
        this.localStorageDataStore = localStorageDataStore;
    }

    /**
     * Checks local storage to see if the data for this repository has been synced for the given branch.
     * Returns all the data about the last sync if it exists.
     * branchID can be null for tables that don't care about the branch.
     * Note that branchID being null does not mean "any branch", it means "no branch". i.e. if "stuff" has been synced
     * for branchID 1, calling getLastSync('stuff', null) will not return info about the sync for branch 1.
     *
     * @param {string} tableName
     * @param {?number} branchID
     *
     * @returns {?{syncedAt: Date}}
     */
    getLastSync(tableName, branchID) {
        const value = this.localStorageDataStore.find(
            this.generateKey(tableName, branchID)
        );

        if (value) {
            value.syncedAt = new Date(value.syncedAt);
        }

        return value || null;
    }

    /**
     * Checks local storage to see if the data for this repository has been synced for the given branch.
     * Returns the Date it was last synced if it exists.
     *
     * @param {string} tableName
     * @param {?number} branchID
     *
     * @return {?Date}
     */
    isSynced(tableName, branchID) {
        let value = this.getLastSync(tableName, branchID);

        return value ? value.syncedAt : null;
    }

    /**
     * Saves the date and branch the last sync happened for in local storage.
     *
     * @param {string} tableName
     * @param {?number} branchID
     */
    saveLastSync(tableName, branchID) {
        let value = {
            syncedAt: (new Date()).toISOString(),
        };

        this.localStorageDataStore.persist(
            this.generateKey(tableName, branchID),
            value
        );
    }

    /**
     * Forget the sync meta data for all branches (including no branch) for a specific table.
     *
     * @param {string} tableName
     */
    remove(tableName) {
        const keys = this.localStorageDataStore.getKeys();
        keys.forEach((key) => {
            if (key.indexOf(`lastSync.${tableName}`) === 0) {
                this.localStorageDataStore.remove(key);
            }
        });
    }

    /**
     * Removes all the sync meta data for all tables from local storage.
     */
    removeAll() {
        const keys = this.localStorageDataStore.getKeys();
        keys.forEach((key) => {
            if (key.indexOf('lastSync.') === 0) {
                this.localStorageDataStore.remove(key);
            }
        });
    }

    /**
     * @private
     *
     * @param {string} tableName
     * @param {number|null} branchID
     *
     * @return {string}
     */
    generateKey(tableName, branchID) {
        let str = `lastSync.${tableName}`;
        if (branchID) {
            str += `.${branchID}`;
        }

        return str;
    }
}

module.exports = SyncStateManager;
